import { v4 as uuid } from 'uuid';

import {
  Dataset,
  Liquidset,
  Plate,
} from 'client/app/apps/disco-prototype/dataStore/dexieStore';
import { LiquidSet } from 'client/app/apps/disco-prototype/state/liquidSet';
import { State } from 'client/app/apps/disco-prototype/state/state';
import { Table, Value } from 'client/app/apps/disco-prototype/state/table';
import { isDefined } from 'common/lib/data';

/**
 * stateFromStore converts a set of tables and liquidsets from the format stored in dexie into the
 * format used on the browser's in-memory state.
 * Ignores any liquidsets which reference unknown tables
 */
export const stateFromStore = (
  data: Dataset[],
  liquidSets: Liquidset[],
  plates: Plate[],
): State => {
  const stateTables = data.map(convertTable);
  const stateLiquidSets = liquidSets
    .map(ls => convertLiquidSet(ls, stateTables, plates))
    .filter(isDefined);

  return {
    tables: stateTables,
    liquidSets: stateLiquidSets,
    steps: [],
  };
};

const convertTable = (dataset: Dataset): Table => {
  // table rows in dexie are still stored as objects, so we have to convert them to arrays
  const columnNames = Object.keys(dataset.rows[0] ?? {});
  return {
    id: dataset.id,
    name: dataset.name,
    columnNames,
    rows: dataset.rows.map(row => columnNames.map(name => row[name] ?? '')),
  };
};

const convertLiquidSet = (
  ls: Liquidset,
  tables: Table[],
  plates: Plate[],
): LiquidSet | undefined => {
  const table = tables.find(t => t.id === ls.table);
  if (!table) {
    console.error(
      `liquid set ${ls.name} references table with id ${ls.table} but none exists`,
    );
    return undefined;
  }

  return {
    name: ls.name,
    id: ls.id,
    table,
    plate: plates.find(plate => plate.id === ls.plate)!,
    liquidProps: {
      // name, well, & plateName are all provided as column names in the original demo
      name: newColumnValue(ls.liquidName),
      well: newColumnValue(ls.well),
      plateName: newColumnValue(ls.plateName),
      // volume cannot be provided in the original demo. Leave it blank to specify that the user hasn't
      // defined it
      volume: newConstantValue(''),
      composition: ls.composition.map(c => ({
        // components have ids - this is purely to make they easier to render as lists
        id: uuid(),
        name: newValue(c.nameIsConstant, c.name),
        concentration: newValue(c.concentrationIsConstant, c.concentration),
      })),
    },
  };
};

const newColumnValue = (name: string): Value => {
  return { selectedMode: 'column', selectedColumnName: name };
};
const newConstantValue = (value: string): Value => {
  return { selectedMode: 'const', value };
};
const newValue = (isConstant: boolean, nameOrValue: string): Value => {
  return isConstant ? newConstantValue(nameOrValue) : newColumnValue(nameOrValue);
};
